import { useEffect, useRef, useState, type ReactNode } from "react";
import { motion, useInView } from "motion/react";
import { Icon, cn, type IconName } from "../../ui/index.tsx";
import { EASE, useCalm } from "./kit.tsx";

const loop = { repeat: Infinity, repeatDelay: 0.8 } as const;

/** Step 1: a progress bar fills while the arrow drops in. */
function InstallScene({ on }: { on: boolean }) {
  return (
    <div className="flex h-full flex-col justify-center gap-3 px-4">
      <div className="flex items-center gap-2 text-xs text-muted">
        <motion.span animate={on ? { y: [-3, 3, -3] } : { y: 0 }} transition={{ duration: 1, repeat: Infinity }}><Icon name="download" className="size-4 text-accent" /></motion.span>
        <span className="font-mono text-[10px] uppercase tracking-widest">Running loader</span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-raised">
        <motion.div className="h-full rounded-full bg-gradient-to-r from-accent to-ember" initial={{ width: "12%" }}
          animate={on ? { width: ["0%", "100%"] } : { width: "12%" }} transition={{ duration: 1.6, ease: "easeInOut", ...loop }} />
      </div>
    </div>
  );
}

/** Step 2: two fields fill in, then the button lights up. */
function SignInScene({ on }: { on: boolean }) {
  return (
    <div className="flex h-full flex-col justify-center gap-2 px-4">
      <div className="h-5 overflow-hidden rounded border border-line bg-bg/60 px-1.5">
        <motion.div className="mt-[7px] h-1.5 rounded-full bg-muted/70" initial={{ width: "20%" }} animate={on ? { width: ["0%", "62%"] } : { width: "20%" }} transition={{ duration: 0.9, ease: "linear", ...loop, repeatDelay: 1.5 }} />
      </div>
      <div className="flex h-5 items-center gap-1 rounded border border-line bg-bg/60 px-1.5">
        {Array.from({ length: 7 }, (_, i) => (
          <motion.span key={i} className="size-1.5 rounded-full bg-muted/70" initial={{ opacity: 0.4 }}
            animate={on ? { opacity: [0, 0, 1, 1, 0] } : { opacity: 0.4 }} transition={{ duration: 2.4, delay: 0.9 + i * 0.08, repeat: Infinity, times: [0, 0.01, 0.02, 0.9, 1] }} />
        ))}
      </div>
      <motion.div className="h-5 rounded bg-accent/80" animate={on ? { opacity: [0.35, 1, 0.35] } : { opacity: 0.5 }} transition={{ duration: 1.2, repeat: Infinity }} />
    </div>
  );
}

/** Step 3: a status dot goes green and the launch button pulses. */
function PlayScene({ on }: { on: boolean }) {
  return (
    <div className="flex h-full items-center justify-between gap-3 px-4">
      <div className="min-w-0 space-y-1.5">
        <div className="h-2 w-20 rounded-full bg-muted/60" />
        <div className="flex items-center gap-1.5 text-[10px] text-ok">
          <motion.span className="size-1.5 rounded-full bg-ok" animate={on ? { scale: [1, 1.7, 1] } : { scale: 1 }} transition={{ duration: 1.2, repeat: Infinity }} />
          <span className="font-mono uppercase tracking-widest">Ready</span>
        </div>
      </div>
      <motion.div className="grid size-10 place-items-center rounded-full bg-accent text-accent-fg" animate={on ? { scale: [1, 1.14, 1], boxShadow: ["0 0 0 0 rgb(255 61 79 / 0.6)", "0 0 0 12px rgb(255 61 79 / 0)", "0 0 0 0 rgb(255 61 79 / 0)"] } : { scale: 1 }}
        transition={{ duration: 1.4, repeat: Infinity }}>
        <svg viewBox="0 0 24 24" className="ml-0.5 size-4 fill-current"><path d="M7 4.5v15l12-7.5z" /></svg>
      </motion.div>
    </div>
  );
}

const STEPS: { icon: IconName; title: string; body: string; scene: (on: boolean) => ReactNode }[] = [
  { icon: "download", title: "Install", body: "Download the loader and run it on your Windows PC.", scene: (on) => <InstallScene on={on} /> },
  { icon: "user", title: "Sign in", body: "Use your ExoWare username and password. Your hardware ID binds on first sign-in.", scene: (on) => <SignInScene on={on} /> },
  { icon: "gamepad", title: "Play", body: "Pick a game. The loader downloads and verifies the files for you.", scene: (on) => <PlayScene on={on} /> },
];

/** Three-step walkthrough; the active step cycles while the section is on screen. */
export function InstallSteps() {
  const ref = useRef<HTMLDivElement>(null);
  const seen = useInView(ref, { once: true, margin: "0px 0px -15% 0px" });
  const visible = useInView(ref, { margin: "0px 0px 0px 0px" });
  const calm = useCalm();
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (!visible || calm) return;
    const t = setInterval(() => setActive((a) => (a + 1) % STEPS.length), 3200);
    return () => clearInterval(t);
  }, [visible, calm]);

  return (
    <section ref={ref}>
      <h2 className="mb-3 text-sm font-semibold">How it works</h2>
      <div className="relative grid gap-4 md:grid-cols-3">
        {/* connector behind the cards: draws left to right */}
        <motion.div className="pointer-events-none absolute left-[16%] right-[16%] top-[3.1rem] hidden h-px origin-left bg-gradient-to-r from-accent via-ember to-accent/30 md:block" aria-hidden
          initial={{ scaleX: 0 }} animate={seen ? { scaleX: 1 } : undefined} transition={{ duration: 1.4, delay: 0.3, ease: EASE }} />
        {STEPS.map((s, i) => {
          const on = i === active && !calm;
          return (
            <motion.button key={s.title} type="button" onClick={() => setActive(i)} aria-pressed={i === active}
              className={cn("pane hud relative !bg-surface p-5 text-left transition-colors", i === active ? "hud-accent border-accent/50" : "hover:border-line-strong")}
              initial={{ opacity: 0, y: 24 }} animate={seen ? { opacity: 1, y: 0 } : undefined} transition={{ duration: 0.55, delay: 0.15 + i * 0.18, ease: EASE }}
              whileHover={{ y: -3 }}>
              <div className="flex items-center gap-3">
                <motion.div className={cn("relative z-10 grid size-11 place-items-center rounded-xl border transition-colors", i === active ? "border-accent bg-accent text-accent-fg" : "border-line-strong bg-raised text-muted")}
                  animate={{ scale: i === active ? 1.08 : 1 }} transition={{ type: "spring", stiffness: 300, damping: 16 }}>
                  <Icon name={s.icon} className="size-5" />
                </motion.div>
                <div>
                  <div className="kicker">Step 0{i + 1}</div>
                  <div className="font-display text-lg font-semibold">{s.title}</div>
                </div>
              </div>
              <div className="mt-4 h-[5.5rem] overflow-hidden rounded-lg border border-line bg-bg/60">{s.scene(on)}</div>
              <p className="mt-4 text-sm leading-relaxed text-muted">{s.body}</p>
            </motion.button>
          );
        })}
      </div>
    </section>
  );
}
